import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { AuthCore } from '../../core/AuthCore';
import { apiService } from '../../services/apiService';
import { Transaction } from '../../types/transaction';

interface TransactionHistorySectionProps {
  limit?: number;
}

const TransactionHistorySection: React.FC<TransactionHistorySectionProps> = ({ limit = 5 }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    fetchTransactions();
  }, [limit]);

  const fetchTransactions = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const token = await AuthCore.getInstance().getAccessToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await apiService.getPurchaseHistory(token, limit, 0);
      
      setTransactions(response.data.history);
      setHasMore(response.data.pagination.hasMore);
    } catch (err) {
      console.error('❌ Failed to fetch transaction history:', err);
      setError(err instanceof Error ? err.message : 'Failed to load transaction history');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };


  const getPayPalTransactionId = (transaction: Transaction) => {
    if (transaction.paypal_capture_id) {
      return transaction.paypal_capture_id;
    }
    if (transaction.paypal_order_id) {
      return transaction.paypal_order_id;
    }
    return null;
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };


  const getStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'Completed';
      case 'pending':
        return 'Pending';
      case 'failed':
        return 'Failed';
      default:
        return status;
    }
  };

  const getTransactionTypeText = (type: string) => {
    switch (type.toLowerCase()) {
      case 'purchase':
        return 'Credit Purchase';
      case 'credit_purchase':
        return 'Credit Purchase';
      case 'topup':
        return 'Top-up';
      case 'membership':
        return 'Membership';
      default:
        return type;
    }
  };

  if (isLoading) {
    return (
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-semibold text-gray-900">Transaction History</CardTitle>
          <p className="text-sm text-gray-600">Your recent credit purchases and transactions</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                  </div>
                  <div className="text-right">
                    <div className="h-4 bg-gray-200 rounded w-16 mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded w-12"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-semibold text-gray-900">Transaction History</CardTitle>
          <p className="text-sm text-gray-600">Your recent credit purchases and transactions</p>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="text-red-500 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button 
              variant="outline" 
              onClick={fetchTransactions}
              className="text-sm"
            >
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-gray-200 shadow-sm">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-xl font-semibold text-gray-900">Transaction History</CardTitle>
            <p className="text-sm text-gray-600">Your recent credit purchases and transactions</p>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchTransactions}
            className="text-sm"
          >
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {transactions.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-gray-600 mb-2">No transactions yet</p>
            <p className="text-sm text-gray-500">Your credit purchases will appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.map((transaction) => (
              <div
                key={transaction.id}
                className="flex justify-between items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h4 className="font-medium text-gray-900">
                      {transaction.package_name || getTransactionTypeText(transaction.type)}
                    </h4>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full border ${
                        transaction.status.toLowerCase() === 'completed' 
                          ? 'text-green-600 bg-green-50 border-green-200'
                          : transaction.status.toLowerCase() === 'pending'
                          ? 'text-yellow-600 bg-yellow-50 border-yellow-200'
                          : transaction.status.toLowerCase() === 'failed'
                          ? 'text-red-600 bg-red-50 border-red-200'
                          : 'text-gray-600 bg-gray-50 border-gray-200'
                      }`}
                    >
                      {getStatusText(transaction.status)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {transaction.credits_purchased} credits • {formatDate(transaction.created_at)}
                  </p>
                  {getPayPalTransactionId(transaction) && (
                    <p className="text-xs text-blue-600 mt-1">
                      PayPal ID: {getPayPalTransactionId(transaction)}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">{formatPrice(transaction.price)}</p>
                </div>
              </div>
            ))}
            
            {hasMore && (
              <div className="text-center pt-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-sm"
                >
                  View All Transactions
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TransactionHistorySection;
